import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { buildWhatsAppLink } from "../constants";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";

const links = [
  { label: "Propuesta", href: "#propuesta" },
  { label: "Productos", href: "#productos" },
  { label: "Tienda", href: "/tienda" },
  { label: "Cómo funciona", href: "#como-funciona" },
  { label: "Historia", href: "#historia" },
  { label: "Equipos B2B", href: "#b2b" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Build the proper href:
  // - Real routes (start with /): pass through unchanged
  // - Anchor links (#section): prepend "/" if not on home (so we navigate home then scroll)
  const buildHref = (href) => {
    if (href.startsWith("/")) return href;
    return isHome ? href : `/${href}`;
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-page/85 backdrop-blur-md border-b border-line" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 flex items-center justify-between h-16 sm:h-20">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <Logo size="sm" variant="dark" />
          <span className="hidden md:inline-block text-[10px] uppercase tracking-[0.3em] text-ink-soft border-l border-line pl-3">
            Fé, Tradición y Amor
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={buildHref(l.href)}
              data-testid={`navlink-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm text-ink hover:text-vino transition-colors link-underline"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle className="hidden sm:inline-flex" />
          <a
            href={buildWhatsAppLink("Hola, me gustaría personalizar una prenda con Vallitas.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-vino text-page px-5 py-2.5 text-sm font-medium hover:bg-vino-deep transition-all hover:-translate-y-0.5"
          >
            Personalizar
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-ink"
            aria-label="Menú"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-page border-t border-line px-6 py-6"
        >
          <nav className="flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={buildHref(l.href)}
                onClick={() => setOpen(false)}
                data-testid={`mobile-navlink-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-base text-ink py-2 border-b border-cream"
              >
                {l.label}
              </a>
            ))}
            <a
              href={buildWhatsAppLink("Hola, me gustaría personalizar una prenda con Vallitas.")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="mobile-whatsapp-btn"
              className="mt-3 inline-flex items-center justify-center rounded-full bg-vino text-page px-6 py-3 text-sm font-medium"
            >
              Personalizar por WhatsApp
            </a>
            <div className="mt-2 flex items-center justify-between text-sm text-ink-soft">
              <span>Tema</span>
              <ThemeToggle />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
